import { Col, Row, type RowProps } from 'antd';
import * as React from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';

const ColStyled = styled(Col)<{ width?: number }>`
  &.ant-col {
    flex: 0 0 ${props => props.width}%;
    max-width: ${props => props.width}%;
    min-width: 0;
    ${down('sm')} {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`;

interface ChildProps {
  colSpan?: number;
}
/**
 * Ottiene la larghezza in percentuale della colonna
 */
const getColWidthPercentage = (percentColSpan: number, props?: ChildProps) => {
  return (props?.colSpan ?? 1) * percentColSpan;
};
/**
 * Ottiene la larghezza di una colonna in percentuale
 * in base al numero di colonne totali della Riga.
 */
const getSingleColWidthPercentage = (children: React.ReactNode) => {
  const rowColSpanList = flattenChildren(children).map((child, index) => {
    if (!React.isValidElement(child) || child == null) return 0;
    const props = child.props as ChildProps;
    return props?.colSpan ?? 1;
  });

  const totalCol = rowColSpanList.reduce((acc, current) => acc + current, 0);

  return 100 / totalCol;
};

export interface RowElementProps extends Omit<RowProps, 'children'> {
  children: React.ReactNode;
}
/**
 * Elememento contenitore di Elemento Riga
 * Ad ogni elemento figlio viene assegnata una colonna di pari misura
 *
 * Se nell'elemento figlio viene specificata la proprieta colSpan
 * viene assegnata la larghezza pari al numero di colonne specificato
 *
 */
export function RowElement(props: RowElementProps) {
  const { children, ...rowsProps } = props;

  const percentColSpan = getSingleColWidthPercentage(children);
  return (
    <Row gutter={24} {...rowsProps}>
      {flattenChildren(props.children).map((child, index) => {
        if (!React.isValidElement(child) || child == null) return null;
        return (
          <ColStyled
            key={index}
            width={getColWidthPercentage(
              percentColSpan,
              child.props as ChildProps,
            )}
          >
            {child}
          </ColStyled>
        );
      })}
    </Row>
  );
}
